// Global Instructions Rule Applied!
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

// Read shapes for Configuration → Stage Actions (view__stage_actions_admin +
// view__stage_action_types). Deliberately separate models from the strict
// pipeline_config / pipeline_stage_entry schemas — stage actions live in their
// own cfg tables precisely so those kanban-critical models stay untouched.

export const StageActionRecipientSchema = z
  .object({
    stage_action_recipients__id: z.string(),
    // 'user' = fixed system user; 'team_role' = dynamic deal-team role
    // (lead / sourced_by / introduced_by) resolved per deal at send time
    stage_action_recipients__recipient_kind: z.string(),
    stage_action_recipients__team_role: z.string().nullish(),
    // Which header the address lands in: 'to' | 'cc'
    stage_action_recipients__address_mode: z.string(),
    system_users__id: z.string().nullish(),
    system_users__name: z.string().nullish(),
    system_users__email: z.string().nullish()
  })
  .strict()

export const StageActionAdminModelSchema = z
  .object({
    stage_actions__id: z.string(),
    stage_actions__pipeline_config_id: z.string(),
    pipelines__id: z.string(),
    pipelines__name: z.string(),
    pipeline_stages__id: z.string(),
    pipeline_stages__name: z.string(),
    stage_action_types__id: z.string(),
    stage_action_types__handler_key: z.string(),
    stage_action_types__label: z.string(),
    stage_action_types__timing: z.string(),
    email_design_templates__id: z.string().nullable(),
    email_design_templates__name: z.string().nullable(),
    stage_actions__params: z.record(z.string(), z.unknown()),
    stage_actions__enabled: z.boolean(),
    recipients: z.array(StageActionRecipientSchema)
  })
  .strict()

export const StageActionTypeModelSchema = z
  .object({
    stage_action_types__id: z.string(),
    stage_action_types__handler_key: z.string(),
    stage_action_types__label: z.string(),
    stage_action_types__description: z.string().nullable(),
    stage_action_types__timing: z.string(),
    stage_action_types__selectable: z.boolean()
  })
  .strict()

export type StageActionRecipient = z.infer<typeof StageActionRecipientSchema>
export type StageActionAdminModel = z.infer<typeof StageActionAdminModelSchema>
export type StageActionTypeModel = z.infer<typeof StageActionTypeModelSchema>

export function validateStageActionAdmin(data: unknown): StageActionAdminModel {
  try {
    return validateWithZodSchema(StageActionAdminModelSchema, data, 'stage action admin model')
  } catch (error) {
    throw new Error(`Stage action admin model validation failed: ${error}`)
  }
}

export function validateStageActionType(data: unknown): StageActionTypeModel {
  try {
    return validateWithZodSchema(StageActionTypeModelSchema, data, 'stage action type model')
  } catch (error) {
    throw new Error(`Stage action type model validation failed: ${error}`)
  }
}
